/* eslint-disable */
import {
  applySpec,
  assoc,
  curry,
  pathOr,
  split,
  compose,
  head,
  last,
  ifElse,
  juxt,
  map,
  converge,
  eqProps,
  filter,
  identity,
  mergeAll,
  xprod,
  equals,
  path,
  prop,
  find,
  complement,
  isNil,
  startsWith,
  useWith,
  concat,
  // tap,
  __,
} from 'ramda';
import Protektor from '@iris-platform/protektor';
import { async } from '@555platform/utility-belt.js';
import uuid from 'uuid/v4';
import {
  IdmuserinfoModel,
  TeamsModel,
  queryTeams,
  queryUsers,
} from '../../models';
import { resolver, roleIdentifierComparator, isRootUserId } from '../util';
import { renameObjectKeys } from '../../util';
import { handleGeneralError } from '../errors';

// Helpers

const matchRoles = pathOr([], ['auminfo', 'roles']);
const roleAsList = split(':');
const validateRole = compose(
  roleList => !!(
    roleList.length === 2
    && head(roleList).length > 1
    && last(roleList).length > 1
  ),
  roleAsList,
);
const throwError = (role) => {
  throw new Error(`Invalid role format: ${role}`);
};
const convertRoleStringToObject = compose(
  list => ({ team_id: head(list), user_role: last(list) }),
  roleAsList,
);
const roleToObject = ifElse(
  validateRole,
  convertRoleStringToObject,
  throwError,
);
const mapRolesToObjects = compose(
  map(roleToObject),
  matchRoles,
);
const checkPair = converge(eqProps('team_id'))([head, last]);
const filterProd = filter(checkPair);
const innerJoinWithMerge = compose(
  map(mergeAll),
  filterProd,
  xprod,
);
const teamToRole = team => Protektor.roleToJSON({
  'roleIdentifier.name': team.user_role,
  'roleIdentifier.team': team.team_id,
});

const translateGraphqlArgsToModelFields = renameObjectKeys(
  prop(__, {
    id: 'team_id',
    displayName: 'display_name',
  }),
);

const startsWithTeamId = useWith(startsWith, [concat(__, ':')]);
const filterRolesByTeam = useWith(filter, [startsWithTeamId]);
const simplifyRoles = map(
  compose(
    last,
    split(':'),
  ),
);
const getTeamScopedRoles = curry(
  compose(
    simplifyRoles,
    filterRolesByTeam,
  ),
);

const userIdFromContext = compose(
  prop('user_id'),
  resolver.ctxProp('user'),
);

export const getTeamScopedUser = curry((teamId, user) => ({
  ...user,
  auminfo: {
    ...user.auminfo,
    roles: getTeamScopedRoles(teamId, user.auminfo.roles),
  },
}));

const findTeamsForUser = async (userID) => {
  const asRoot = isRootUserId(userID);
  const Idmuserinfo = IdmuserinfoModel();
  const Teams = TeamsModel();
  const findUser = asRoot
    ? Idmuserinfo.findRootUser().lean()
    : Idmuserinfo.findUserByUserID(userID).lean();
  const findTeams = Teams.findAllTeams().lean();
  let foundUser;
  let foundTeams;

  try {
    [foundUser, foundTeams] = await Promise.all([findUser, findTeams]);
  } catch (error) {
    if (error) {
      return handleGeneralError(error);
    }
  }
  const roleObjs = mapRolesToObjects(foundUser);

  // Compare teams reported by the role and teams stored to make sure that roles
  // and team list is not out of sync, basically perform innerJoin with merge on team lists
  const actualTeams = asRoot
    ? map(assoc('user_role', 'N/A'), foundTeams)
    : innerJoinWithMerge(roleObjs, foundTeams);

  // get all roles for each team
  const promisedRoles = map(teamToRole, actualTeams);
  const userRoles = await Promise.all(promisedRoles);
  const filteredRoles = filter(complement(isNil), userRoles);

  // finally add user role for each team
  const finalTeams = map((team) => {
    const roleForTeam = find(
      compose(
        equals(
          roleIdentifierComparator({
            name: team.user_role,
            team: team.team_id,
          }),
        ),
        roleIdentifierComparator,
        path(['roleIdentifier']),
      ),
      filteredRoles,
    );

    return {
      ...team,
      team_role: roleForTeam,
    };
  }, actualTeams);

  return finalTeams;
};

const persistTeam = compose(
  async.catchWith(() => {
    throw new Error('Duplicate team');
  }),
  queryTeams('createTeam'),
);

// const associateTeamToUser = flip(queryUsers('addUserToTeam'));

// Resolvers

const team = resolver.compose(
  queryTeams('findOne'),
  translateGraphqlArgsToModelFields,
  resolver.args,
);

const teams = resolver.compose(findTeamsForUser, resolver.argProp('userID'));

const teamsForCurrentUser = resolver.compose(
  findTeamsForUser,
  userIdFromContext,
);

const teamList = resolver.authenticatedResolver.createResolver(
  async (root, { tfilter, skip, limit }) => {
    const teaminfo = TeamsModel();
    const findPortalTeams = teaminfo
      .findTeamsByDisplayName(tfilter, skip, limit)
      .lean();
    const findPortalTeamsCount = teaminfo
      .findTeamsCountByDisplayName(tfilter)
      .lean();
    let foundTeams;
    let foundTeamsCount;

    try {
      [foundTeams, foundTeamsCount] = await Promise.all([
        findPortalTeams,
        findPortalTeamsCount,
      ]);
    } catch (error) {
      if (error) {
        return handleGeneralError(error);
      }
    }

    return {
      teamList: foundTeams,
      skip,
      limit: foundTeams.length,
      count: foundTeamsCount,
    };
  },
);

const createTeam = resolver.authenticatedResolver.createResolver(
  compose(
    persistTeam,
    assoc('team_id', uuid()),
    resolver.argProp('team'),
  ),
);

const usersForTeam = resolver.compose(
  async.apply(
    applySpec({
      team: identity,
      users: useWith(map, [
        compose(
          getTeamScopedUser,
          prop('team_id'),
        ),
      ]),
    }),
  ),
  juxt([queryTeams('findTeamById'), queryUsers('findUsersByTeam')]),
  resolver.argProp('id'),
);

const searchTeams = resolver.compose(
  converge(
    queryTeams('findTeams'),
    [
      resolver.argProp('filter'),
      resolver.argProp('skip'),
      resolver.argProp('limit'),
    ],
  ),
);

export default {
  Query: {
    team,
    teams,
    teamsForCurrentUser,
    usersForTeam,
    teamList,
    searchTeams,
  },
  Mutation: {
    createTeam,
  },
};
